import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Copy, Globe, Star, Clock, ExternalLink, Smartphone, Monitor } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

interface SERPData {
  title: string;
  description: string;
  url: string;
  displayUrl: string;
  breadcrumbs: string[];
  sitelinks: Array<{
    title: string;
    description: string;
    url: string;
  }>;
  richSnippet: {
    type: string;
    rating: number;
    reviewCount: number;
    price: string;
    availability: string;
    publishDate: string;
    author: string;
  };
}

const SERPSnippetPreviewTool: React.FC = () => {
  const [serpData, setSerpData] = useState<SERPData>({
    title: '',
    description: '',
    url: '',
    displayUrl: '',
    breadcrumbs: [],
    sitelinks: [],
    richSnippet: {
      type: 'none',
      rating: 4.5,
      reviewCount: 127,
      price: '$29.99',
      availability: 'In Stock',
      publishDate: '2024-01-15',
      author: 'John Smith'
    }
  });

  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'mobile'>('desktop');
  const [showRichSnippets, setShowRichSnippets] = useState(false);

  useSEO({
    title: 'SERP Snippet Preview Tool - Google Search Result Preview',
    description: 'Preview how your pages will appear in Google search results. Optimize title tags, meta descriptions, and rich snippets for better CTR.',
    keywords: 'SERP preview, Google search preview, meta description preview, title tag preview, rich snippets, search results',
    canonical: 'https://snap-tools.online/seo/serp-snippet-preview'
  });

  // Load example data
  const loadExample = () => {
    setSerpData({
      title: 'Best Wireless Headphones 2024 - Top 10 Reviews & Buying Guide',
      description: 'Discover the best wireless headphones of 2024. Expert reviews, comparisons, and buying guide. Free shipping on orders over $50. Shop now and save 25%!',
      url: 'https://example.com/best-wireless-headphones-2024',
      displayUrl: 'https://example.com › reviews › headphones',
      breadcrumbs: ['Home', 'Electronics', 'Audio', 'Headphones'],
      sitelinks: [
        {
          title: 'Top Rated Headphones',
          description: 'Browse our highest rated wireless headphones',
          url: '/top-rated'
        },
        {
          title: 'Budget Options',
          description: 'Best wireless headphones under $100',
          url: '/budget'
        },
        {
          title: 'Premium Collection',
          description: 'High-end audiophile headphones',
          url: '/premium'
        },
        {
          title: 'Customer Reviews',
          description: 'Read real customer reviews and ratings',
          url: '/reviews'
        }
      ],
      richSnippet: {
        type: 'product',
        rating: 4.7,
        reviewCount: 1234,
        price: '$199.99',
        availability: 'In Stock',
        publishDate: '2024-01-15',
        author: 'Tech Expert Team'
      }
    });
    setShowRichSnippets(true);
    toast.success('Example SERP data loaded');
  };

  // Add breadcrumb
  const addBreadcrumb = () => {
    setSerpData(prev => ({
      ...prev,
      breadcrumbs: [...prev.breadcrumbs, '']
    }));
  };

  // Update breadcrumb
  const updateBreadcrumb = (index: number, value: string) => {
    setSerpData(prev => ({
      ...prev,
      breadcrumbs: prev.breadcrumbs.map((crumb, i) => i === index ? value : crumb)
    }));
  };

  // Remove breadcrumb
  const removeBreadcrumb = (index: number) => {
    setSerpData(prev => ({
      ...prev,
      breadcrumbs: prev.breadcrumbs.filter((_, i) => i !== index)
    }));
  };

  // Add sitelink
  const addSitelink = () => {
    setSerpData(prev => ({
      ...prev,
      sitelinks: [...prev.sitelinks, { title: '', description: '', url: '' }]
    }));
  };

  // Update sitelink
  const updateSitelink = (index: number, field: keyof SERPData['sitelinks'][0], value: string) => {
    setSerpData(prev => ({
      ...prev,
      sitelinks: prev.sitelinks.map((link, i) => 
        i === index ? { ...link, [field]: value } : link
      )
    }));
  };

  // Remove sitelink
  const removeSitelink = (index: number) => {
    setSerpData(prev => ({
      ...prev,
      sitelinks: prev.sitelinks.filter((_, i) => i !== index)
    }));
  };

  // Generate meta tags
  const generateMetaTags = () => {
    let tags = '';
    if (serpData.title) tags += `<title>${serpData.title}</title>\n`;
    if (serpData.description) tags += `<meta name="description" content="${serpData.description}" />\n`;
    if (serpData.url) tags += `<link rel="canonical" href="${serpData.url}" />\n`;
    return tags;
  };

  // Copy meta tags
  const copyMetaTags = async () => {
    const tags = generateMetaTags();
    try {
      await navigator.clipboard.writeText(tags);
      toast.success('Meta tags copied to clipboard');
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy meta tags');
    }
  };

  // Render star rating
  const renderStars = (rating: number) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 !== 0;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />);
    }

    if (hasHalfStar) {
      stars.push(<Star key="half" className="w-4 h-4 fill-yellow-400 text-yellow-400 opacity-50" />);
    }

    for (let i = stars.length; i < 5; i++) {
      stars.push(<Star key={i} className="w-4 h-4 text-gray-300" />);
    }

    return stars;
  };

  // Desktop SERP Preview
  const DesktopSERPPreview = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-2xl">
      {/* Main Result */}
      <div className="mb-4">
        {/* Breadcrumbs */}
        {serpData.breadcrumbs.length > 0 && (
          <div className="text-sm text-gray-600 mb-1 flex items-center">
            <Globe className="w-4 h-4 mr-1" />
            {serpData.breadcrumbs.join(' › ')}
          </div>
        )}

        {/* URL */}
        <div className="text-sm text-green-700 mb-1">
          {serpData.displayUrl || serpData.url || 'https://example.com'}
        </div>

        {/* Title */}
        <h3 className="text-xl text-blue-600 hover:underline cursor-pointer mb-2 leading-tight">
          {serpData.title || 'Page Title Appears Here'}
        </h3>

        {/* Rich Snippet */}
        {showRichSnippets && serpData.richSnippet.type !== 'none' && (
          <div className="mb-2">
            {serpData.richSnippet.type === 'product' && (
              <div className="flex items-center gap-3 text-sm">
                <div className="flex items-center gap-1">
                  <div className="flex">{renderStars(serpData.richSnippet.rating)}</div>
                  <span className="text-gray-600">
                    {serpData.richSnippet.rating} ({serpData.richSnippet.reviewCount} reviews)
                  </span>
                </div>
                <span className="text-gray-600">•</span>
                <span className="font-semibold">{serpData.richSnippet.price}</span>
                <span className="text-green-600">{serpData.richSnippet.availability}</span>
              </div>
            )}

            {serpData.richSnippet.type === 'article' && (
              <div className="flex items-center gap-3 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {new Date(serpData.richSnippet.publishDate).toLocaleDateString()}
                </div>
                <span>•</span>
                <span>By {serpData.richSnippet.author}</span>
              </div>
            )}
          </div>
        )}

        {/* Description */}
        <p className="text-gray-700 text-sm leading-relaxed">
          {serpData.description || 'Meta description appears here. This is what users see in search results to decide whether to click on your page.'}
        </p>

        {/* Sitelinks */}
        {serpData.sitelinks.length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            {serpData.sitelinks.slice(0, 4).map((sitelink, index) => (
              <div key={index} className="text-sm">
                <div className="text-blue-600 hover:underline cursor-pointer">
                  {sitelink.title}
                </div>
                <div className="text-gray-600 text-xs">
                  {sitelink.description}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // Mobile SERP Preview
  const MobileSERPPreview = () => (
    <div className="bg-white border border-gray-200 rounded-lg p-4 max-w-sm">
      {/* URL */}
      <div className="text-xs text-green-700 mb-1">
        {serpData.displayUrl || serpData.url || 'https://example.com'}
      </div>

      {/* Title */}
      <h3 className="text-lg text-blue-600 hover:underline cursor-pointer mb-2 leading-tight">
        {serpData.title || 'Page Title Appears Here'}
      </h3>

      {/* Rich Snippet */}
      {showRichSnippets && serpData.richSnippet.type !== 'none' && (
        <div className="mb-2">
          {serpData.richSnippet.type === 'product' && (
            <div className="text-sm">
              <div className="flex items-center gap-1 mb-1">
                <div className="flex">{renderStars(serpData.richSnippet.rating)}</div>
                <span className="text-gray-600 text-xs">
                  {serpData.richSnippet.rating} ({serpData.richSnippet.reviewCount})
                </span>
              </div>
              <div className="font-semibold">{serpData.richSnippet.price}</div>
            </div>
          )}
        </div>
      )}

      {/* Description */}
      <p className="text-gray-700 text-sm leading-relaxed">
        {serpData.description || 'Meta description appears here. This is what users see in search results.'}
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-indigo-100 py-8">
      <div className="container mx-auto px-4 max-w-6xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-6 md:p-8"
        >
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
              <Search className="w-8 h-8 text-purple-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">SERP Snippet Preview Tool</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Preview how your pages will appear in Google search results. Optimize your title tags and meta descriptions to improve click-through rates.
            </p>
          </div>

          <AdContainer 
            position="header"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
            className="mb-6"
          />

          {/* Quick Actions */}
          <div className="flex gap-3 justify-center mb-8 flex-wrap">
            <button
              onClick={loadExample}
              className="flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Globe className="w-4 h-4" />
              Load Example
            </button>
            <button
              onClick={copyMetaTags}
              className="flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Copy className="w-4 h-4" />
              Copy Meta Tags
            </button>
            <button
              onClick={() => setShowRichSnippets(!showRichSnippets)}
              className={`flex items-center gap-2 px-6 py-3 rounded-lg transition-colors ${
                showRichSnippets 
                  ? 'bg-yellow-600 text-white hover:bg-yellow-700' 
                  : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Star className="w-4 h-4" />
              Rich Snippets
            </button>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-8">
            {/* Input Form */}
            <div className="space-y-6">
              {/* Basic Information */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Page Title *
                    </label>
                    <input
                      type="text"
                      value={serpData.title}
                      onChange={(e) => setSerpData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Your page title"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      maxLength={60}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{serpData.title.length}/60 characters</span>
                      <span className={serpData.title.length > 60 ? 'text-red-500' : 'text-green-500'}>
                        {serpData.title.length > 60 ? 'Too long' : 'Good length'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Meta Description *
                    </label>
                    <textarea
                      value={serpData.description}
                      onChange={(e) => setSerpData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Your meta description"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      rows={3}
                      maxLength={160}
                    />
                    <div className="flex justify-between text-xs text-gray-500 mt-1">
                      <span>{serpData.description.length}/160 characters</span>
                      <span className={serpData.description.length > 160 ? 'text-red-500' : 'text-green-500'}>
                        {serpData.description.length > 160 ? 'Too long' : 'Good length'}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Page URL
                    </label>
                    <input
                      type="url"
                      value={serpData.url}
                      onChange={(e) => setSerpData(prev => ({ ...prev, url: e.target.value }))}
                      placeholder="https://example.com/page"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display URL (optional)
                    </label>
                    <input
                      type="text"
                      value={serpData.displayUrl}
                      onChange={(e) => setSerpData(prev => ({ ...prev, displayUrl: e.target.value }))}
                      placeholder="https://example.com › category › page"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* Breadcrumbs */}
              <div className="bg-blue-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-blue-900">Breadcrumbs</h3>
                  <button
                    onClick={addBreadcrumb}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Add Breadcrumb
                  </button>
                </div>
                <div className="space-y-3">
                  {serpData.breadcrumbs.map((crumb, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={crumb}
                        onChange={(e) => updateBreadcrumb(index, e.target.value)}
                        placeholder={`Breadcrumb ${index + 1}`}
                        className="flex-1 p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                      <button
                        onClick={() => removeBreadcrumb(index)}
                        className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 transition-colors"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  {serpData.breadcrumbs.length === 0 && (
                    <p className="text-blue-700 text-sm">No breadcrumbs added yet.</p>
                  )}
                </div>
              </div>

              {/* Sitelinks */}
              <div className="bg-green-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-green-900">Sitelinks</h3>
                  <button
                    onClick={addSitelink}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    Add Sitelink
                  </button>
                </div>
                <div className="space-y-4">
                  {serpData.sitelinks.map((sitelink, index) => (
                    <div key={index} className="border border-green-200 rounded-lg p-4 bg-white">
                      <div className="flex justify-between items-center mb-3">
                        <span className="font-medium text-green-900">Sitelink {index + 1}</span>
                        <button
                          onClick={() => removeSitelink(index)}
                          className="bg-red-500 text-white px-2 py-1 rounded hover:bg-red-600 transition-colors"
                        >
                          ×
                        </button>
                      </div>
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={sitelink.title}
                          onChange={(e) => updateSitelink(index, 'title', e.target.value)}
                          placeholder="Sitelink title"
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={sitelink.description}
                          onChange={(e) => updateSitelink(index, 'description', e.target.value)}
                          placeholder="Sitelink description"
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          value={sitelink.url}
                          onChange={(e) => updateSitelink(index, 'url', e.target.value)}
                          placeholder="/sitelink-url"
                          className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        />
                      </div>
                    </div>
                  ))}
                  {serpData.sitelinks.length === 0 && (
                    <p className="text-green-700 text-sm">No sitelinks added yet.</p>
                  )}
                </div>
              </div>

              {/* Rich Snippets */}
              {showRichSnippets && (
                <div className="bg-yellow-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-yellow-900 mb-4">Rich Snippet Data</h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Snippet Type
                      </label>
                      <select
                        value={serpData.richSnippet.type}
                        onChange={(e) => setSerpData(prev => ({
                          ...prev,
                          richSnippet: { ...prev.richSnippet, type: e.target.value }
                        }))}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      >
                        <option value="none">None</option>
                        <option value="product">Product</option>
                        <option value="article">Article</option>
                        <option value="recipe">Recipe</option>
                        <option value="review">Review</option>
                        <option value="event">Event</option>
                      </select>
                    </div>

                    {serpData.richSnippet.type === 'product' && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rating
                          </label>
                          <input
                            type="number"
                            min="0"
                            max="5"
                            step="0.1"
                            value={serpData.richSnippet.rating}
                            onChange={(e) => setSerpData(prev => ({
                              ...prev,
                              richSnippet: { ...prev.richSnippet, rating: parseFloat(e.target.value) || 0 }
                            }))}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Review Count
                          </label>
                          <input
                            type="number"
                            value={serpData.richSnippet.reviewCount}
                            onChange={(e) => setSerpData(prev => ({
                              ...prev,
                              richSnippet: { ...prev.richSnippet, reviewCount: parseInt(e.target.value) || 0 }
                            }))}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Price
                          </label>
                          <input
                            type="text"
                            value={serpData.richSnippet.price}
                            onChange={(e) => setSerpData(prev => ({
                              ...prev,
                              richSnippet: { ...prev.richSnippet, price: e.target.value }
                            }))}
                            placeholder="$29.99"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Availability
                          </label>
                          <select
                            value={serpData.richSnippet.availability}
                            onChange={(e) => setSerpData(prev => ({
                              ...prev,
                              richSnippet: { ...prev.richSnippet, availability: e.target.value }
                            }))}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          >
                            <option value="In Stock">In Stock</option>
                            <option value="Out of Stock">Out of Stock</option>
                            <option value="Limited Stock">Limited Stock</option>
                            <option value="Pre-order">Pre-order</option>
                          </select>
                        </div>
                      </div>
                    )}

                    {serpData.richSnippet.type === 'article' && (
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Publish Date
                          </label>
                          <input
                            type="date"
                            value={serpData.richSnippet.publishDate}
                            onChange={(e) => setSerpData(prev => ({
                              ...prev,
                              richSnippet: { ...prev.richSnippet, publishDate: e.target.value }
                            }))}
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Author
                          </label>
                          <input
                            type="text"
                            value={serpData.richSnippet.author}
                            onChange={(e) => setSerpData(prev => ({
                              ...prev,
                              richSnippet: { ...prev.richSnippet, author: e.target.value }
                            }))}
                            placeholder="Author name"
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="space-y-6">
              {/* Device Selector */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Search Result Preview</h3>
                
                <div className="flex bg-white rounded-lg p-1 mb-6">
                  <button
                    onClick={() => setPreviewDevice('desktop')}
                    className={`flex-1 py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2 ${
                      previewDevice === 'desktop' 
                        ? 'bg-purple-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Monitor className="w-4 h-4" />
                    Desktop
                  </button>
                  <button
                    onClick={() => setPreviewDevice('mobile')}
                    className={`flex-1 py-2 px-4 rounded-md transition-colors flex items-center justify-center gap-2 ${
                      previewDevice === 'mobile' 
                        ? 'bg-purple-600 text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    <Smartphone className="w-4 h-4" />
                    Mobile
                  </button>
                </div>

                {/* Preview Display */}
                <div className="flex justify-center">
                  {previewDevice === 'desktop' ? <DesktopSERPPreview /> : <MobileSERPPreview />}
                </div>

                {/* Statistics */}
                <div className="mt-6 bg-white rounded-lg p-4">
                  <h4 className="font-medium text-gray-900 mb-3">Preview Statistics</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Title Length:</span>
                      <span className={`ml-2 ${serpData.title.length > 60 ? 'text-red-600' : 'text-green-600'}`}>
                        {serpData.title.length}/60
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Description Length:</span>
                      <span className={`ml-2 ${serpData.description.length > 160 ? 'text-red-600' : 'text-green-600'}`}>
                        {serpData.description.length}/160
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Breadcrumbs:</span>
                      <span className="ml-2">{serpData.breadcrumbs.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Sitelinks:</span>
                      <span className="ml-2">{serpData.sitelinks.length}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Generated Meta Tags */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">Generated Meta Tags</h3>
                  <button
                    onClick={copyMetaTags}
                    className="flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <Copy className="w-4 h-4" />
                    Copy
                  </button>
                </div>
                
                <div className="bg-white rounded-lg border p-4 h-32 overflow-y-auto">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap font-mono">
                    {generateMetaTags()}
                  </pre>
                </div>
              </div>

              {/* CTR Tips */}
              <div className="bg-green-50 rounded-lg p-6">
                <h4 className="font-semibold text-green-900 mb-3">CTR Optimization Tips</h4>
                <ul className="text-sm text-green-800 space-y-2">
                  <li>• <strong>Action Words:</strong> Use verbs like "discover", "learn", "get"</li>
                  <li>• <strong>Numbers:</strong> Include specific numbers and statistics</li>
                  <li>• <strong>Emotional Words:</strong> Use "amazing", "essential", "ultimate"</li>
                  <li>• <strong>Questions:</strong> Address user intent directly</li>
                  <li>• <strong>Benefits:</strong> Focus on what users will gain</li>
                  <li>• <strong>Urgency:</strong> Create sense of urgency when appropriate</li>
                </ul>
              </div>

              {/* Testing Tools */}
              <div className="bg-blue-50 rounded-lg p-6">
                <h4 className="font-semibold text-blue-900 mb-3">Testing Tools</h4>
                <div className="space-y-3">
                  <a
                    href="https://search.google.com/test/rich-results"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-700 hover:text-blue-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Google Rich Results Test
                  </a>
                  <a
                    href="https://search.google.com/search-console"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-700 hover:text-blue-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Google Search Console
                  </a>
                  <a
                    href="https://developers.google.com/search/docs/appearance/structured-data"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-700 hover:text-blue-800"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Structured Data Guidelines
                  </a>
                </div>
              </div>
            </div>
          </div>

          <AdContainer 
            position="footer"
            adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
            className="mb-6"
          />

          {/* Educational Content */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-purple-50 rounded-lg p-6">
              <h4 className="font-semibold text-purple-900 mb-3">SERP Optimization Best Practices</h4>
              <ul className="text-sm text-purple-800 space-y-2">
                <li>• <strong>Title Tag:</strong> 50-60 characters, include primary keyword</li>
                <li>• <strong>Meta Description:</strong> 150-160 characters, compelling CTA</li>
                <li>• <strong>Rich Snippets:</strong> Implement structured data markup</li>
                <li>• <strong>Sitelinks:</strong> Optimize internal linking structure</li>
                <li>• <strong>Breadcrumbs:</strong> Implement breadcrumb navigation</li>
                <li>• <strong>Mobile-First:</strong> Optimize for mobile search results</li>
              </ul>
            </div>

            <div className="bg-indigo-50 rounded-lg p-6">
              <h4 className="font-semibold text-indigo-900 mb-3">Common SERP Features</h4>
              <ul className="text-sm text-indigo-800 space-y-2">
                <li>• <strong>Featured Snippets:</strong> Position zero results</li>
                <li>• <strong>People Also Ask:</strong> Related question boxes</li>
                <li>• <strong>Local Pack:</strong> Local business results with map</li>
                <li>• <strong>Image Pack:</strong> Image results for visual queries</li>
                <li>• <strong>Video Results:</strong> Video thumbnails in results</li>
                <li>• <strong>Shopping Results:</strong> Product listings with prices</li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default SERPSnippetPreviewTool;
